import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import SiteHeader from "@/components/feature/SiteHeader";
import Footer from "@/pages/home/components/Footer";

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email.includes("@")) return setError("Please enter a valid email address.");
    if (!password) return setError("Please enter your password.");
    const result = login(email.trim(), password);
    if (!result) {
      return setError("No account found with those credentials. Check your email and password, or sign up.");
    }
    navigate(result.type === "employer" ? "/dashboard/employer" : "/dashboard");
  };

  const fillDemo = (type: "worker" | "employer") => {
    if (type === "worker") {
      setEmail("marcus@example.com");
      setPassword("demo123");
    } else {
      setEmail("sarah@buildfast.co");
      setPassword("demo123");
    }
    setError("");
  };

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SiteHeader />
      <main className="pt-28 pb-16 min-h-screen flex items-center">
        <div className="w-full max-w-5xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">

          {/* Left: Visual side */}
          <div className="hidden md:block">
            <div className="relative rounded-2xl overflow-hidden h-[520px]">
              <img
                src="https://public.readdy.ai/ai/img_res/3e10b3a305f742ce2d51df9565b3ca3e.jpg"
                alt="Gig n' Cash"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8">
                <h2 className="text-2xl font-extrabold text-white mb-2">
                  Income opportunities,<br />ready when you are.
                </h2>
                <p className="text-sm text-white/70">
                  Thousands of workers and employers trust Gig <span className="italic">n&apos;</span> Cash every day.
                </p>
                <div className="flex gap-4 mt-5">
                  {[
                    { label: "Workers", value: "12,400+" },
                    { label: "Jobs Posted", value: "3,800+" },
                    { label: "Avg Match", value: "94%" },
                  ].map(s => (
                    <div key={s.label}>
                      <div className="text-lg font-extrabold text-white">{s.value}</div>
                      <div className="text-xs text-white/60">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right: Login Form */}
          <div>
            <div className="mb-8">
              <span className="inline-block text-xs font-bold uppercase tracking-widest text-emerald-600 bg-emerald-50 px-3.5 py-1.5 rounded-full mb-4">
                Welcome Back
              </span>
              <h1 className="text-3xl font-extrabold text-gray-900 mb-1">Sign in to Gig <span className="italic">n&apos;</span> Cash</h1>
              <p className="text-sm text-gray-500">
                No account yet?{" "}
                <Link to="/signup" className="text-emerald-600 font-semibold hover:underline">
                  Sign up free
                </Link>
              </p>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-8">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1.5">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-semibold text-gray-700">Password</label>
                    <a href="#" className="text-xs text-emerald-600 hover:underline">Forgot password?</a>
                  </div>
                  <div className="relative">
                    <input
                      type={showPass ? "text" : "password"}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Your password"
                      className="w-full border border-gray-200 rounded-lg px-3.5 py-2.5 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:border-emerald-400 transition-colors"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPass(!showPass)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"} />
                    </button>
                  </div>
                </div>

                {error && (
                  <div className="flex items-start gap-2 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2.5">
                    <i className="ri-error-warning-line mt-0.5 flex-shrink-0" />
                    <span>{error}</span>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full py-3 rounded-lg text-sm font-bold text-white bg-emerald-500 hover:bg-emerald-600 transition-colors whitespace-nowrap"
                >
                  Sign In
                  <i className="ri-arrow-right-line ml-2" />
                </button>
              </form>

              {/* Demo Quick Login */}
              <div className="mt-6 pt-5 border-t border-gray-100">
                <p className="text-xs text-gray-500 text-center mb-3 font-medium">
                  Try a demo account — no sign-up needed
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => fillDemo("worker")}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-user-3-line text-emerald-600 text-sm" />
                    <span className="text-xs font-semibold text-gray-700">Worker Demo</span>
                  </button>
                  <button
                    onClick={() => fillDemo("employer")}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-building-2-line text-gray-700 text-sm" />
                    <span className="text-xs font-semibold text-gray-700">Employer Demo</span>
                  </button>
                </div>
                <p className="text-center text-xs text-gray-400 mt-3">
                  Click a demo above, then hit Sign In
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
